import type { PaginatedDataSyncJobResponse } from './validations/types';

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { camelcaseKeysFromApi } from '@vestigas/data';

import { vestigasApiNoConversion } from '~/services/kyClient';

import { queryKeysDataSyncJob } from './queryKeys';
import {
  parseDataSyncResponse,
  rawPaginatedResponseSchema,
} from './responseShapes';
import { jobFromApiKeepingUserKeys } from './userKeyedFields';

/**
 * Fetches data sync jobs without any filters or pagination.
 *
 * @returns Promise containing job data with default pagination from API
 */
export const fetchDataSyncJobs =
  async (): Promise<PaginatedDataSyncJobResponse> => {
    const rawResponse = await vestigasApiNoConversion
      .get(ENDPOINT.DATA_SYNC.GET_ALL_JOBS())
      .json<unknown>();

    // Convert pagination fields and each job (with nested tasks)
    const { data, ...paginationFields } = parseDataSyncResponse(
      rawPaginatedResponseSchema,
      rawResponse,
      'job list',
    );

    return {
      ...camelcaseKeysFromApi(paginationFields),
      data: data.map(jobFromApiKeepingUserKeys),
    } as PaginatedDataSyncJobResponse;
  };

/**
 * React Query hook for fetching data sync jobs.
 *
 * @param options - Additional React Query options
 * @returns Query result containing list of data sync jobs
 */
export const useQueryDataSyncJobs = (
  options?: Omit<
    UseQueryOptions<PaginatedDataSyncJobResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryKey = queryKeysDataSyncJob.getAll();

  return useQuery({
    queryFn: fetchDataSyncJobs,
    queryKey,
    staleTime: ms('5m'), // Cache for 5 minutes
    ...options,
  });
};
